import { useEffect, useMemo, useRef, useState } from 'react';
import { login } from '@/utils/auth';
import { modalStore } from '@/stores/modal.store';
import useUserStore from '@/stores/user.store';
import { ApiRequest } from '@/utils/axios';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { playSound } from '@/utils/playSound';
import { notify } from '@/utils/notify';
import { requireTradeLink } from '@/utils/requireTradeLink';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { soundsVolume, JACKPOT_SKIN_LIMIT } from '@/utils/constants';
import { syncedNow } from '@/utils/timesync';
import { IJackpotGame } from '@/utils/types/IJackpotHistoryGame';
import { IJackpotPlayer } from '@/utils/types/IJackpotPlayer';
import { IGameItem } from '@/utils/types/Items';
import CurrentRound from './CurrentRound';
import WheelStage from './WheelStage';

type JackpotState = IJackpotGame & { paused?: number; pausedRemainingMs?: number | null };

interface JackpotRes {
    state: JackpotState;
    items: IGameItem[];
    users: IJackpotPlayer[];
    minbet: string;
}

// Renders remaining seconds as MM:SS. A jackpot round is 60s, so the timer
// runs 01:00 -> 00:00. (The old `00:${sec}` form also broke for any value
// >= 100s, e.g. an over-extended round once showed the malformed "00:101".)
function formatTimer(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm < 10 ? '0' : ''}${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

const DEFAULT_STATE: IJackpotGame = {
    id: -1,
    value: 0,
    hash: 'unknown',
    status: 1,
    winnerUser: null,
    finishedAt: null,
    endsAt: null,
    rollAt: null,
    revealAt: null,
    clearAt: null,
    winnerAngle: null,
};

const Jackpot = () => {
    const sounds = modalStore((state) => state.sounds);
    const { setTitle, setData, toggleModal, setComponent } = modalStore((state) => state);
    const { isAuthed, tradeLink, user } = useUserStore((state) => state);

    const betAudio = useRef<HTMLAudioElement | null>(null);
    const winAudio = useRef<HTMLAudioElement | null>(null);
    const rollAudio = useRef<HTMLAudioElement | null>(null);
    const soundsEnabled = useRef(sounds);

    useEffect(() => {
        soundsEnabled.current = sounds;
    }, [sounds]);

    const [jackpot, setJackpot] = useState<IJackpotGame>(DEFAULT_STATE);
    const [items, setItems] = useState<IGameItem[]>([]);
    const [users, setUsers] = useState<IJackpotPlayer[]>([]);
    const [value, setValue] = useState(0);
    const [winnerBlockShown, setWinnerBlockShown] = useState(false);
    const [winner, setWinner] = useState<IJackpotPlayer | undefined>(undefined);
    const [timer, setTimer] = useState('Waiting for users');
    const [paused, setPaused] = useState(0);
    const [pausedRemainingMs, setPausedRemainingMs] = useState<number | null>(null);

    const minbet = useRef(1);

    const itemsByUser = useMemo(() => {
        const acc: Record<number, IGameItem[]> = {};
        for (const it of items) (acc[it.userId] ??= []).push(it);
        return acc;
    }, [items]);

    async function getJackpot() {
        const {
            data: { response },
        }: {
            data: { response: JackpotRes };
        } = await ApiRequest.get('/jackpot');

        setItems(response.items ?? []);
        setUsers(response.users ?? []);
        minbet.current = parseFloat(response.minbet);
        setJackpot(response.state);
        setValue(response.state.value);
        setPaused(response.state.paused ?? 0);
        // While fully paused the server sends endsAt=null and a constant
        // pausedRemainingMs snapshot, which drives the frozen timer display.
        setPausedRemainingMs(response.state.pausedRemainingMs ?? null);

        if (response.state.winnerUser) {
            setWinner(response.state.winnerUser);
        }
    }

    useEffect(() => {
        getJackpot().then();
    }, []);

    // Timer display — interpolated locally from endsAt + syncedNow().
    // Refreshes ~5x/s so the seconds counter feels smooth.
    useEffect(() => {
        const { endsAt, rollAt, revealAt, clearAt } = jackpot;
        const now = syncedNow();

        // Winner phase: show ROLL during spin, then "Waiting for users" once it clears
        if (rollAt && revealAt && clearAt && now < clearAt) {
            setTimer('ROLL');
            return;
        }

        // Admin fully froze the round (state=1). While paused the server sends
        // endsAt=null and a constant pausedRemainingMs snapshot, so the display
        // stays put — no drift across reloads, no climbing as bets land.
        if (paused === 1) {
            if (pausedRemainingMs == null) {
                // Paused before the timer ever started (still waiting for 2nd player).
                setTimer('Waiting for users');
            } else {
                setTimer(formatTimer(Math.ceil(pausedRemainingMs / 1000)));
            }
            return;
        }

        // Idle (no timer yet)
        if (!endsAt) {
            setTimer('Waiting for users');
            return;
        }

        const tick = () => {
            const left = endsAt - syncedNow();
            if (left <= 1000) {
                setTimer('ROLL');
            } else {
                setTimer(formatTimer(Math.ceil(left / 1000)));
            }
        };
        tick();
        const iv = window.setInterval(tick, 200);
        return () => window.clearInterval(iv);
    }, [jackpot.endsAt, jackpot.rollAt, jackpot.revealAt, jackpot.clearAt, paused, pausedRemainingMs]);

    // Winner-block visibility + win sound — driven by absolute revealAt / clearAt
    useEffect(() => {
        const { rollAt, revealAt, clearAt, winnerUser: w } = jackpot;
        if (!rollAt || !revealAt || !clearAt) {
            setWinnerBlockShown(false);
            return;
        }

        if (w) setWinner(w);

        const now = syncedNow();
        const revealIn = revealAt - now;
        const clearIn = clearAt - now;

        const timers: number[] = [];

        if (revealIn <= 0) {
            // Already past reveal — show immediately, no sound (post-refresh)
            setWinnerBlockShown(true);
        } else {
            timers.push(
                window.setTimeout(() => {
                    setWinnerBlockShown(true);
                    if (winAudio.current) {
                        winAudio.current.volume = soundsVolume;
                        soundsEnabled.current && winAudio.current.play().catch(() => {});
                    }
                }, revealIn)
            );
        }

        if (clearIn > 0) {
            timers.push(
                window.setTimeout(() => {
                    setWinnerBlockShown(false);
                    setWinner(undefined);
                    setJackpot((prev) => ({
                        ...prev,
                        rollAt: null,
                        revealAt: null,
                        clearAt: null,
                        winnerAngle: null,
                        winnerUser: null,
                        finishedAt: null,
                    }));
                }, clearIn)
            );
        } else {
            // Already past clearAt — wipe immediately
            setWinnerBlockShown(false);
            setWinner(undefined);
        }

        return () => timers.forEach((t) => window.clearTimeout(t));
    }, [jackpot.rollAt, jackpot.revealAt, jackpot.clearAt, jackpot.winnerUser?.id]);

    useSubscription(
        'jackpotCreated',
        ({ id, hash, endsAt }: { id: number; hash: string; endsAt: number | null }) => {
            setJackpot({
                ...DEFAULT_STATE,
                id,
                hash,
                endsAt: endsAt ?? null,
            });
            setItems([]);
            setUsers([]);
            setValue(0);
            setPaused(0);
            setPausedRemainingMs(null);
            setWinnerBlockShown(false);
            setWinner(undefined);
        }
    ).then();

    useSubscription('jackpotBet', (data: JackpotRes) => {
        setItems(data.items);
        setUsers(data.users);
        playSound(betAudio, soundsEnabled.current);
        setJackpot(data.state);
        if (value === 0) setValue(data.state.value);
        // getState() is authoritative on every bet — re-sync pause so the
        // frozen timer stays put while bets land during an admin pause.
        setPaused(data.state.paused ?? 0);
        setPausedRemainingMs(data.state.pausedRemainingMs ?? null);
    }).then();

    useSubscription('jackpotValue', (v: number) => setValue(v)).then();

    // The server doesn't emit jackpotTimer while fully paused, so endsAt here
    // always refers to a live, running deadline.
    useSubscription('jackpotTimer', ({ endsAt }: { endsAt: number }) => {
        setJackpot((prev) => ({ ...prev, endsAt }));
    }).then();

    useSubscription(
        'jackpotPause',
        ({
            state,
            endsAt,
            pausedRemainingMs: incomingPausedMs,
        }: {
            state: number;
            endsAt: number | null;
            pausedRemainingMs?: number | null;
        }) => {
            setPaused(state);
            // On pause the server sends endsAt=null; on resume it sends the
            // fresh live deadline. Mirror it verbatim.
            setJackpot((prev) => ({ ...prev, endsAt: endsAt ?? null }));
            setPausedRemainingMs(state === 1 ? incomingPausedMs ?? null : null);
        }
    ).then();

    useSubscription(
        'jackpotFinished',
        (payload: {
            winnerUser: IJackpotPlayer;
            value: number;
            status: number;
            rollAt: number;
            revealAt: number;
            clearAt: number;
            winnerAngle: number;
        }) => {
            setValue(payload.value);
            setJackpot((prev) => ({
                ...prev,
                winnerUser: payload.winnerUser,
                status: payload.status,
                rollAt: payload.rollAt,
                revealAt: payload.revealAt,
                clearAt: payload.clearAt,
                winnerAngle: payload.winnerAngle,
                finishedAt: payload.revealAt,
            }));
        }
    ).then();

    function siteDeposit() {
        setTitle('ADD FUNDS');
        setComponent('Jackpot site deposit');
    }
    function steamDeposit() {
        setTitle('ADD FUNDS');
        setComponent('Jackpot steam deposit');
    }
    function onJoin() {
        if (!isAuthed) {
            login();
            return;
        }
        if (!tradeLink()) {
            requireTradeLink();
            return;
        }
        setTitle('MAKE DEPOSIT');
        setComponent('Make deposit');
        toggleModal();
        setData({
            siteFunc: siteDeposit,
            steamFunc: steamDeposit,
            minbet: minbet.current,
        });
    }
    function onLogin() {
        login();
    }

    function openAdminBet() {
        setTitle('ADD BET (ADMIN)');
        setComponent('Admin add bet');
        toggleModal(true);
    }

    return (
        <div className="flex flex-col gap-3 min-w-0">
            {user.status === 'MODERATOR' && (
                <div className="surface flex items-center justify-between gap-3 px-4 py-2">
                    <span className="text-[12px] font-semibold tracking-[0.06em] text-text_2">
                        Admin tools
                    </span>
                    <button
                        type="button"
                        onClick={openAdminBet}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-fl text-[12px] font-bold tracking-[0.06em] text-white hover:brightness-110 transition-all"
                        style={{
                            background: 'linear-gradient(180deg, #ff7d6e, #f04e3a)',
                            boxShadow:
                                '0 6px 16px rgba(240,78,58,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                        }}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.4]"
                        >
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        ADD BET
                    </button>
                </div>
            )}
            {/* Arena — circle + stats on the left, current round players on the right */}
            <section className="surface grid grid-cols-[660px_minmax(0,1fr)] max-[1536px]:grid-cols-1 gap-6 p-6 max-md:p-4 min-w-0">
                <div className="flex flex-col items-center gap-5 min-w-0 max-[1536px]:max-w-[570px] max-[1536px]:mx-auto max-[1536px]:w-full">
                    <WheelStage
                        gameId={jackpot.id}
                        bank={value}
                        users={users}
                        minbet={minbet.current}
                        winnerUser={winner}
                        winnerBlock={winnerBlockShown}
                        rollAt={jackpot.rollAt}
                        revealAt={jackpot.revealAt}
                        winnerAngle={jackpot.winnerAngle}
                        clearAt={jackpot.clearAt}
                        timer={timer}
                        isAuthed={isAuthed}
                        onJoin={onJoin}
                        onLogin={onLogin}
                        rollAudio={rollAudio}
                        soundsEnabled={soundsEnabled}
                    />

                    {/* stat tiles: Round / Skin Limit / Min / Game */}
                    <div className="grid grid-cols-4 gap-3 w-full">
                        {[
                            { k: 'Round', v: `#${jackpot.id}` },
                            { k: 'Skin Limit', v: `${JACKPOT_SKIN_LIMIT}` },
                            { k: 'Min', v: `$${minbet.current}` },
                            { k: 'Game', v: 'Classic' },
                        ].map((s, i) => {
                            const colors = ['#3ad6ff', '#8b6dff', '#ffc24d', '#46e08a'];
                            return (
                                <div
                                    key={s.k}
                                    className="rounded-fl border border-stroke text-center px-3 py-3.5"
                                    style={{
                                        background: 'rgba(139,109,255,0.07)',
                                        boxShadow: 'inset 0 0 18px rgba(139,109,255,0.08)',
                                    }}
                                >
                                    <div className="text-[10.5px] tracking-[0.12em] uppercase text-text_3">
                                        {s.k}
                                    </div>
                                    <div
                                        className="text-lg font-extrabold mt-1.5 tabular-nums"
                                        style={{
                                            color: colors[i],
                                            textShadow: `0 0 14px ${colors[i]}99`,
                                        }}
                                    >
                                        {s.v}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <CurrentRound
                    users={users}
                    itemsByUser={itemsByUser}
                    bank={value}
                    gameId={jackpot.id}
                    highlightUserId={winnerBlockShown ? winner?.id : undefined}
                />
            </section>

            <audio ref={betAudio} src="/audio/jackpot-bet.mp3" />
            <audio ref={winAudio} src="/audio/win.mp3" />
            <audio ref={rollAudio} src="/audio/jackpot-roll.wav" />
        </div>
    );
};

export default Jackpot;
